"use client";

import { Skeleton } from "@/components/ui/skeleton";
import { ITEM_CARD_SKELETON_STRIP_WIDTH, itemCard } from "@/lib/item-card-tokens";
import { cn } from "@/lib/utils";

type ItemCardSkeletonTone = "default" | "inverse";

const BASE_ITEM_WRAPPER_CLASS = "w-[148px] shrink-0 min-w-0 sm:w-[152px] md:w-[168px] lg:w-[180px]";

function toneClassName(tone: ItemCardSkeletonTone): string {
  if (tone === "inverse") {
    return "bg-white/10";
  }
  return "bg-muted dark:bg-white/5";
}

export function ItemCardSkeleton({
  className,
  tone = "default",
}: {
  className?: string;
  tone?: ItemCardSkeletonTone;
}) {
  const toneClass = toneClassName(tone);
  const L = itemCard.layout;

  return (
    <div className={cn("flex min-w-0 flex-col", className)} style={{ gap: L.shellGap }}>
      <Skeleton
        className={cn("aspect-4/5 w-full animate-none", toneClass)}
        style={{ borderRadius: L.photoRadiusPx }}
      />
      <div
        className="flex flex-col"
        style={{ gap: L.titleToPriceGap, minHeight: L.bodyMinHeight }}
      >
        <Skeleton
          className={cn("animate-none", toneClass)}
          style={{ height: L.titleBlockHeight, borderRadius: 10 }}
        />
        <div
          className="flex items-center justify-between gap-2"
          style={{ height: L.bottomRowHeight }}
        >
          <Skeleton
            className={cn("animate-none", toneClass)}
            style={{ height: L.priceLineHeight, borderRadius: 6, width: "52%" }}
          />
          <Skeleton
            className={cn("shrink-0 animate-none rounded-full", toneClass)}
            style={{ width: 72, height: 32 }}
          />
        </div>
      </div>
    </div>
  );
}

export function ItemCardSkeletonRow({
  count,
  keyPrefix,
  tone = "default",
  containerClassName,
  rowClassName,
  itemClassName,
}: {
  count: number;
  keyPrefix: string;
  tone?: ItemCardSkeletonTone;
  containerClassName?: string;
  rowClassName?: string;
  itemClassName?: string;
}) {
  return (
    <div
      className={cn(
        "scrollbar-hide -mx-4 overflow-x-auto overscroll-x-contain px-4 md:mx-0 md:px-0",
        containerClassName,
      )}
    >
      <div className={cn("flex w-max gap-2.5 pb-1 md:gap-3", rowClassName)}>
        {Array.from({ length: count }).map((_, index) => (
          <div key={`${keyPrefix}-${index}`} className={cn(BASE_ITEM_WRAPPER_CLASS, itemClassName)}>
            <ItemCardSkeleton tone={tone} />
          </div>
        ))}
      </div>
    </div>
  );
}

const GRID_CELL_WRAPPER_CLASS = "h-auto min-w-0";

export function ItemCardSkeletonGrid({
  count,
  keyPrefix,
  tone = "default",
  gridClassName,
  itemClassName,
  inline = false,
}: {
  count: number;
  keyPrefix: string;
  tone?: ItemCardSkeletonTone;
  gridClassName?: string;
  itemClassName?: string;
  /** Render direct grid children (no wrapper grid). Use inside an existing item-card grid. */
  inline?: boolean;
}) {
  const cells = Array.from({ length: count }).map((_, index) => (
    <div key={`${keyPrefix}-${index}`} className={GRID_CELL_WRAPPER_CLASS}>
      <ItemCardSkeleton tone={tone} className={itemClassName} />
    </div>
  ));

  if (inline) {
    return <>{cells}</>;
  }

  return <div className={gridClassName}>{cells}</div>;
}

export { ITEM_CARD_SKELETON_STRIP_WIDTH };
